import { FundingTexts } from '@services/MarppleShop/renderApp/components/cells/funding/FundingTexts.ts';
import { ScrollManagerBuilder } from '../../../../../../shared/front-helper/scroll';
import { map, pipe, toArray } from '@fxts/core';
import { FundingSupporterItem } from '@services/MarppleShop/renderApp/components/cells/funding/FundingSection.ts';
import {
  FundingModalRanking,
  FundingModalCreatedAt,
} from '@services/MarppleShop/renderApp/components/cells/funding/FundingModalBody.ts';
import { CustomEventWithoutDetail, html } from 'rune-ts';
import axios, { AxiosResponse } from 'axios';
import { FundingModalProps } from '@services/MarppleShop/renderApp/components/cells/funding/FundingModal.ts';
import { Alert } from '@services/MarppleShop/renderApp/components/cells/Alert/Alert.ts';
import { getResizedUrl } from '@services/MarppleShop/shared/util/image.ts';
import {
  RecentSupportersResponse,
  SupporterRankingsResponse,
  RecentSupportersParams,
  SupporterRankingsParams,
  SupporterItem,
} from '@services/MarppleShop/features/Funding';
import { ScrollManager } from '../../../../../../shared/front-helper/scroll/managers';

class ChangeFundingModalTabEvent extends CustomEventWithoutDetail {}

export class FundingModalController {
  public readonly texts: ReturnType<typeof FundingTexts.texts>;
  private props: FundingModalProps;
  // 랭킹순 무한 스크롤 매니저
  public rankingInfiniteScrollManager?: ScrollManager<SupporterItem, [Date, number | null]>;

  // 최신순 무한 스크롤 매니저
  public createdAtInfiniteScrollManager?: ScrollManager<SupporterItem, Date>;

  // 현재 선택된 모달 탭
  private selected_tab = FundingModalRanking.toString();
  // 랭킹 무한 스크롤 리스트에서 렌더할 아이템의 순위
  private rank: number;

  constructor(props: FundingModalProps) {
    this.props = props;
    this.rank = props.supporters.data.length;
    this.texts = FundingTexts.texts();
  }

  createRankingInfiniteScrollManager() {
    if (this.rankingInfiniteScrollManager) return;
    try {
      this.rankingInfiniteScrollManager = ScrollManagerBuilder.create()
        .loader(this.loaderOfRanking.bind(this))
        .normal()
        .container(`.${this.ranking_container}`)
        .cursor(this.props.supporters.next_cursor)
        .render(this.onRenderRanking.bind(this))
        .hasMore(this.props.supporters.has_more)
        .onError(this.onError.bind(this))
        .build();
    } catch (_) {}
  }

  createCreatedAtInfiniteScrollManager() {
    if (this.createdAtInfiniteScrollManager) return;
    try {
      this.createdAtInfiniteScrollManager = ScrollManagerBuilder.create()
        .loader(this.loaderOfCreatedAt.bind(this))
        .normal()
        .container(`.${this.created_at_container}`)
        .cursor(new Date())
        .render(this.onRenderCreatedAt.bind(this))
        .onError(this.onError.bind(this))
        .build();
    } catch (_) {}
  }

  async loaderOfRanking(cursor: [Date, number | null]): Promise<SupporterRankingsResponse> {
    const [created_at, amount] = cursor;

    const { data: res } = await axios.post<
      SupporterRankingsParams,
      AxiosResponse<{ data: SupporterRankingsResponse }>
    >(
      '/@api/funding/supporter-rankings',
      {
        created_at: `${created_at}`,
        amount,
        product_id: this.props.product_id,
        limit: this.limit,
      },
      {
        headers: {
          'Content-Type': 'application/json',
        },
      },
    );

    return res.data;
  }

  async loaderOfCreatedAt(cursor: Date): Promise<RecentSupportersResponse> {
    const { data: res } = await axios.post<
      RecentSupportersParams,
      AxiosResponse<{ data: RecentSupportersResponse }>
    >(
      '/@api/funding/supporters',
      {
        created_at: `${cursor}`,
        product_id: this.props.product_id,
        limit: this.limit,
      },
      {
        headers: {
          'Content-Type': 'application/json',
        },
      },
    );

    return res.data;
  }

  private onRenderRanking(data: SupporterRankingsResponse['data']) {
    return data.map((funding) => {
      ++this.rank;
      return new FundingSupporterItem({ ...funding, rank: this.rank }).render();
    });
  }

  private onRenderCreatedAt(data: RecentSupportersResponse['data']) {
    return pipe(
      data,
      map((funding) => new FundingSupporterItem(funding).render()),
      toArray,
    );
  }

  private async onError(error: Error) {
    await Alert.open({ title: 'Error', message: error.message });
  }

  public setSelectedTab(tab: string) {
    this.selected_tab = tab;
  }

  public getSelectedTab() {
    return this.selected_tab;
  }

  public getProps() {
    return this.props;
  }

  public getRank() {
    return this.rank;
  }

  /**
   * 랭킹 탭 무한 스크롤  컨테이너
   */
  get ranking_container() {
    return `${FundingModalRanking.toString()}-container`;
  }

  /**
   * 최신순 탭 무한 스크롤  컨테이너
   */
  get created_at_container() {
    return `${FundingModalCreatedAt.toString()}-container`;
  }

  /**
   * 펀딩 모달 탭
   */
  get tabs() {
    return [
      { name: this.texts.modal.tabs.ranking, key: FundingModalRanking.toString() },
      { name: this.texts.modal.tabs.created_at, key: FundingModalCreatedAt.toString() },
    ];
  }

  /**
   * 랭킹 / 최신순 API 데이터 요청 갯수
   */
  get limit() {
    return 100;
  }

  static readonly ChangeFundingModalTabEvent = ChangeFundingModalTabEvent;

  static readonly FundingSupporterRankThreeBadge =
    's3.marpple.co/files/u_5248713/2025/6/original/Rank=Bronze.png';

  static readonly FundingSupporterRankTwoBadge =
    's3.marpple.co/files/u_5248713/2025/6/original/Rank=Silver.png';

  static readonly FundingSupporterRankOneBadge =
    's3.marpple.co/files/u_5248713/2025/6/original/Rank=Gold.png';

  static makeRankHtml(rank: number) {
    const FUNDING_SUPPORTER_RANK_BADGE: Record<number, string> = {
      1: FundingModalController.FundingSupporterRankOneBadge,
      2: FundingModalController.FundingSupporterRankTwoBadge,
      3: FundingModalController.FundingSupporterRankThreeBadge,
    };

    if (rank && FUNDING_SUPPORTER_RANK_BADGE[rank])
      return html`<img
        src="${getResizedUrl({
          url: FUNDING_SUPPORTER_RANK_BADGE[rank],
          quality: 85,
          width: 70 * 2,
        })}"
        alt=""
      />`;

    return rank;
  }
}
